import type { MbscCalendarEvent } from '@akoom-tech/mobiscroll-react';
import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  endOfDay,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { DAYS_OF_WEEK } from '@/utils/months-and-days';
import {
  ButtonColors,
  DayOfWeek,
  LabelColors,
  OffDaysRequestType,
} from '@/types';
import { DayOff, OffdayRecurrence } from '../types';

type DateTimeValue = string | number | Date | null | undefined;

const dayCodes = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

export const PROVIDER_AVAILABILTY_TABLE_HEAD = [
  { id: 'emptyCell' },
  { id: 'id', label: 'ID', align: 'left' },
  { id: 'type_id', label: 'Type', align: 'left' },
  { id: 'start_date', label: 'Start Date', align: 'left' },
  { id: 'end_date', label: 'End Date', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'notes', label: 'Notes', align: 'left' },
  { id: 'offday_recurrence', label: 'Recurrence', align: 'left' },
  { id: 'offday_recurrence.week_days', label: 'Recurring days', align: 'left' },
  { id: 'actionBtns', label: '', align: 'left' },
];

// TODO: ask for colors
export const calendarLabelColorPicker = (id: number) => {
  switch (id) {
    case 1:
      return 'red';
    case 2:
      return 'green';
    case 3:
      return 'blue';
    default:
      return 'yellow';
  }
};

export const timeoffStatusColorPicker = (status: string): LabelColors => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
    default:
      return 'error';
  }
};

export const formatRepeatString = (
  offday_recurrence: OffdayRecurrence | null | undefined
) => {
  if (!offday_recurrence || !offday_recurrence?.repeat_string) {
    return 'N/A';
  }
  if (
    offday_recurrence.repeat_string === 'weekly' &&
    offday_recurrence.interval_number === 2
  ) {
    return 'fortnightly';
  }
  return offday_recurrence?.repeat_string;
};

export const generateEventLabel = (dayOff: DayOff) => {
  if (!dayOff.offday_recurrence) {
    return dayOff.offday_request_types.description.toUpperCase();
  } else {
    return `${dayOff.offday_request_types.description.toUpperCase()} - ${formatRepeatString(
      dayOff.offday_recurrence
    )}`;
  }
};

export type ProviderAvailabilityCalendarEvent = MbscCalendarEvent & {
  offdayId: number;
};

type BuildProviderAvailabilityCalendarEventsOptions = {
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  selectedTimeOffId?: string | null;
};

const toLondonDate = (value: DateTimeValue) => {
  if (!value) return null;

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return new Date(
    parsedDate.toLocaleString('en-US', { timeZone: 'Europe/London' })
  );
};

const getDateKey = (date: Date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}${String(date.getDate()).padStart(2, '0')}`;

const getVisibleDays = (rangeStart: Date, rangeEnd: Date) => {
  const days: Date[] = [];
  for (
    let cursor = startOfDay(rangeStart);
    cursor <= endOfDay(rangeEnd);
    cursor = addDays(cursor, 1)
  ) {
    days.push(cursor);
  }
  return days;
};

const getWeekDays = (value?: string | null) =>
  new Set(
    (value ?? '')
      .split(',')
      .map((day) => day.trim())
      .filter(Boolean)
  );

const getRepeat = (recurrence: OffdayRecurrence) =>
  recurrence.repeat_string.toLowerCase();

const getInterval = (recurrence: OffdayRecurrence) =>
  getRepeat(recurrence) === 'fortnightly'
    ? 2
    : recurrence.interval_number || 1;

const dateMax = (left: Date, right: Date) => (left > right ? left : right);
const dateMin = (left: Date, right: Date) => (left < right ? left : right);

export const buildProviderAvailabilityCalendarEvents = (
  daysOff: DayOff[],
  {
    rangeEnd,
    rangeStart,
    selectedTimeOffId,
  }: BuildProviderAvailabilityCalendarEventsOptions = {}
): ProviderAvailabilityCalendarEvent[] => {
  const visibleRangeStart = rangeStart ? startOfDay(rangeStart) : null;
  const visibleRangeEnd = rangeEnd ? endOfDay(rangeEnd) : null;

  return daysOff.flatMap((dayOff) => {
    const startRef = toLondonDate(dayOff.start_date);
    const endRef = toLondonDate(dayOff.end_date);

    if (!startRef || !endRef) return [];

    const isSelected = selectedTimeOffId === String(dayOff.id);
    const buildEvent = (
      start: Date,
      end: Date,
      occurrenceKey?: string
    ): ProviderAvailabilityCalendarEvent => ({
      id: occurrenceKey ? `${dayOff.id}-${occurrenceKey}` : dayOff.id,
      offdayId: dayOff.id,
      title: generateEventLabel(dayOff),
      start,
      end,
      color: calendarLabelColorPicker(dayOff.type_id),
      cssClass: isSelected
        ? 'provider-availability-event-selected'
        : undefined,
    });

    if (!visibleRangeStart || !visibleRangeEnd) {
      return [buildEvent(startRef, endRef)];
    }

    const recurrence = dayOff.offday_recurrence;
    const visibleDays = getVisibleDays(visibleRangeStart, visibleRangeEnd);

    if (!recurrence) {
      if (endRef < visibleRangeStart || startRef > visibleRangeEnd) return [];

      return visibleDays
        .map((day) => {
          const dayStart = startOfDay(day);
          const dayEnd = endOfDay(day);
          const start = dateMax(startRef, dayStart);
          const end = dateMin(endRef, dayEnd);

          return start < end ? buildEvent(start, end, getDateKey(day)) : null;
        })
        .filter(
          (event): event is ProviderAvailabilityCalendarEvent =>
            event !== null
        );
    }

    const recurrenceStart = toLondonDate(recurrence.from_date) ?? startRef;
    const recurrenceUntil = toLondonDate(recurrence.until);
    const repeat = getRepeat(recurrence);
    const interval = getInterval(recurrence);
    const weekDays = getWeekDays(recurrence.week_days);

    return visibleDays
      .map((day) => {
        if (day < startOfDay(recurrenceStart)) return null;
        if (recurrenceUntil && day > endOfDay(recurrenceUntil)) return null;

        if (repeat === 'weekly' || repeat === 'fortnightly') {
          const dayCode = dayCodes[day.getDay()];
          if (weekDays.size && !weekDays.has(dayCode)) return null;

          const weekDiff = differenceInCalendarWeeks(
            startOfWeek(day, { weekStartsOn: 1 }),
            startOfWeek(recurrenceStart, { weekStartsOn: 1 }),
            { weekStartsOn: 1 }
          );
          if (weekDiff % interval !== 0) return null;
        } else if (repeat === 'daily') {
          const dayDiff = differenceInCalendarDays(
            startOfDay(day),
            startOfDay(recurrenceStart)
          );
          if (dayDiff % interval !== 0) return null;
        } else if (!isSameDay(day, startRef)) {
          return null;
        }

        const occurrenceStart = new Date(day);
        occurrenceStart.setHours(
          startRef.getHours(),
          startRef.getMinutes(),
          0,
          0
        );

        const occurrenceEnd = new Date(day);
        occurrenceEnd.setHours(endRef.getHours(), endRef.getMinutes(), 0, 0);
        if (occurrenceEnd <= occurrenceStart) {
          occurrenceEnd.setDate(occurrenceEnd.getDate() + 1);
        }

        return buildEvent(occurrenceStart, occurrenceEnd, getDateKey(day));
      })
      .filter(
        (event): event is ProviderAvailabilityCalendarEvent => event !== null
      );
  });
};

// Define type for the mapping object
type DayIdMap = { [key: string]: number };

// Function to sort days based on predefined order in DAYS_OF_WEEK
export const sortRecurringDays = (
  offday_recurrence: OffdayRecurrence | null | undefined
): string[] => {
  if (!offday_recurrence?.week_days) return [];
  const arrayOfDays = (offday_recurrence?.week_days).split(', ');
  // Create a mapping from recurrence_short_name to id
  const dayIdMap: DayIdMap = DAYS_OF_WEEK.reduce<DayIdMap>((acc, day) => {
    acc[day.recurrence_short_name] = day.id;
    return acc;
  }, {});

  // Sort the array based on the ids from the mapping
  arrayOfDays.sort((a, b) => dayIdMap[a] - dayIdMap[b]);

  return arrayOfDays;
};

export const returnTypeName = (
  typeId: number,
  requestTypes: OffDaysRequestType[]
) =>
  requestTypes.find((type) => type.id === typeId)?.description ??
  'Type not found';

export const isProviderAvailabilityExpired = (
  dayOff: Pick<DayOff, 'end_date' | 'offday_recurrence' | 'start_date'>,
  referenceDate: Date = new Date()
) => {
  if (dayOff.offday_recurrence) {
    return dayOff.offday_recurrence.until
      ? new Date(dayOff.offday_recurrence.until) < referenceDate
      : false;
  }

  const expiryDate = dayOff.end_date ?? dayOff.start_date;
  return expiryDate ? new Date(expiryDate) < referenceDate : false;
};

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

const parseDateTimeValue = (value: DateTimeValue) => {
  if (!value) return null;

  const parsedDate = value instanceof Date ? value : new Date(value);
  return isValidDate(parsedDate) ? parsedDate : null;
};

const formatLocalTime = (date: Date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;

export const formatProviderAvailabilityDate = (date: DateTimeValue) => {
  const parsedDate = parseDateTimeValue(date);
  if (!parsedDate) return 'N/A';

  const dateFormatOptionsConfig: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
  };
  // !recurring &&
  //   Object.assign(dateFormatOptionsConfig, {
  //     hour: '2-digit',
  //     minute: '2-digit',
  //   });
  return parsedDate.toLocaleDateString('en-gb', dateFormatOptionsConfig);
};

export const formatStartEndTimeForTableRowLabel = (
  start: DateTimeValue,
  end: DateTimeValue
) => {
  const formatFixedTime = (value: DateTimeValue) => {
    if (!value) return 'N/A';

    if (value instanceof Date) {
      return isValidDate(value) ? formatLocalTime(value) : 'N/A';
    }

    if (typeof value === 'string') {
      const matchedTime = value.match(/(?:T|\s)(\d{2}):(\d{2})/);
      if (matchedTime) {
        return `${matchedTime[1]}:${matchedTime[2]}`;
      }
    }

    const parsedDate = parseDateTimeValue(value);
    if (!parsedDate) return 'N/A';

    return parsedDate.toLocaleTimeString('en-gb', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  };

  const formattedStart = formatFixedTime(start);
  const formattedEnd = formatFixedTime(end);
  return `${formattedStart} - ${formattedEnd}`;
};

// this is not needed anymore, we can just use the `start_date` for all
// export const formatStartEndDay = (
//   dateType: 'start' | 'end',
//   dayOff: DayOff
// ): string => {
//   const { start_date, end_date, offday_recurrence } = dayOff;
//   if (!offday_recurrence) {
//     if (dateType === 'start') {
//       return start_date ? formatProviderAvailabilityDate(start_date) : 'N/A';
//     } else {
//       return end_date ? formatProviderAvailabilityDate(end_date) : 'N/A';
//     }
//   } else {
//     if (dateType === 'start') {
//       return offday_recurrence.from_date
//         ? formatProviderAvailabilityDate(offday_recurrence.from_date)
//         : 'N/A';
//     } else {
//       return offday_recurrence.until
//         ? formatProviderAvailabilityDate(offday_recurrence.until)
//         : 'indefinite';
//     }
//   }
// };

export const formatDaysOfWeekFromApi = (days: string): DayOfWeek[] => {
  const arrayOfDays = days.split(', ');
  return DAYS_OF_WEEK.filter((day) =>
    arrayOfDays.includes(day.recurrence_short_name)
  );
};

// TODO: extend this to be reusable loadingButtonText, disagreeButtonText... etc
export const createDialog = (
  name: string,
  actionVerb: string,
  title: string,
  loadingButtonColor: ButtonColors,
  agreeFunction: () => void
) => ({
  name,
  actionVerb,
  title,
  loadingButtonColor,
  disagreeButtonText: 'Cancel',
  agreeFunction,
});

export const TIME_OFF_STATUSES = [
  {
    id: 1,
    name: 'approved',
    actionVerb: 'Approve',
    color: 'success',
    icon: 'eva:checkmark-outline',
  },
  {
    id: 2,
    name: 'pending',
    actionVerb: 'Set pending',
    color: 'warning',
    icon: 'eva:pause-circle-outline',
  },
  {
    id: 3,
    name: 'rejected',
    actionVerb: 'Reject',
    color: 'error',
    icon: 'eva:close-outline',
  },
];
